import App from '../../../app';
import { ComplianceActionTypes } from '../../../app/package/base/service/activation-flow/activation-flow-domain';
import { ModalType } from '../../../app/package/base/service/modals/modal-service';
import {
  LOCAL_STORAGE_KEY_FORCE_5G_MINI_GAME,
  LOCAL_STORAGE_KEY_USE_5G_MINI_GAME,
  SESSION_STORAGE_KEY_DISABLE_MINI_GAME_INTERCEPT,
} from '../../../constants';
import { localStorage_getOrNull } from '../../base/package/util/localStorage';
import { sessionStorage_getOrNull } from '../../base/package/util/sessionStorage';
import { UIGamificationMaintenanceModalNew } from '../../components/drawers/gamification-maintenance-modal-new/ui-gamification-maintenance-modal-new';
import { IncentiveModalContainer } from '../../components/drawers/incentive-modal-container/incentive-modal-container';
import type { IncentivePageView } from '../types';
import { isMiniGameTracked, MINI_GAME_OFFER_NAME_5G_WHEEL_DEFAULT, openMiniGameModal } from './mini-game-helpers';

export async function openGamification(isModalView = false) {
  if (!App.instance.activationFlow.isActionEnabled(ComplianceActionTypes.INCENTIVE)) {
    App.instance.activationFlowModalService.requestUiUpdate();
    return;
  }
  // Dev override: force mini-game modal regardless of backend state (requires 5G mini game to be enabled)
  if (
    localStorage_getOrNull(LOCAL_STORAGE_KEY_USE_5G_MINI_GAME) &&
    localStorage_getOrNull(LOCAL_STORAGE_KEY_FORCE_5G_MINI_GAME)
  ) {
    openMiniGameModal({
      miniGame: {
        id: 'dev-forced',
        groupId: 'dev',
        status: 'in_progress' as any,
        offerId: 'dev',
        offerName: MINI_GAME_OFFER_NAME_5G_WHEEL_DEFAULT,
        uniqueIdentifier: 'dev-forced',
        content: { termsAndConditions: '' },
      },
    });
    return;
  }

  const isGamificationMaintenance = isGamificationMaintenanceEnabled();

  if (isGamificationMaintenance) {
    const modal = new UIGamificationMaintenanceModalNew();
    window.$app.modal.show(modal, ModalType.GlobalStatusModal);
    return;
  }

  // An untracked mini-game hijacks the gamification entry point: instead of the
  // incentive view the user gets the wheel. That is unpredictable for automated
  // tests, so QA can opt out via sessionStorage and always land on the incentive
  // view. Only this opportunistic intercept is suppressed — opening the game
  // explicitly (mini-game card, deep link, dev panel) still works.
  const miniGame = await window.$app.incentiveApi.signal.miniGame.getValue();
  if (
    miniGame &&
    !sessionStorage_getOrNull(SESSION_STORAGE_KEY_DISABLE_MINI_GAME_INTERCEPT) &&
    !isMiniGameTracked(miniGame.id) &&
    App.instance.activationFlow.isActionEnabled(ComplianceActionTypes.GAMEPLAY)
  ) {
    openMiniGameModal({ miniGame, openIncentiveModalViewOnFinish: isModalView });
    return;
  }

  const unclaimedRewards = await window.$app.incentiveApi.signal.incentiveRewards.getValue();

  if (unclaimedRewards?.[0]?.id) {
    const modal = new IncentiveModalContainer();
    modal.initialView = { view: 'reward-claim', id: unclaimedRewards[0].id };
    modal.navigateToPageOnExit = !isModalView;
    window.$app.modal.show(modal, ModalType.IncentiveModal);
    return;
  }

  if (isModalView) {
    const modal = new IncentiveModalContainer();
    window.$app.modal.show(modal, ModalType.IncentiveModal);
    return;
  }

  App.instance.router.navigateToIncentive();
}

export function openDeepLinkInGamification(view: IncentivePageView, id = '') {
  if (!App.instance.activationFlow.isActionEnabled(ComplianceActionTypes.INCENTIVE)) {
    App.instance.activationFlowModalService.requestUiUpdate();
    return;
  }
  const modal = new IncentiveModalContainer();
  modal.initialView = { view, id };
  window.$app.modal.show(modal, ModalType.IncentiveModal);
}

export function isGamificationMaintenanceEnabled(): boolean {
  return !!window.$app.casinoConfig.gamificationMaintenanceEnabled;
}

export function isGamificationDisabled(): boolean {
  return !!window.$app.casinoConfig.gamificationDisabled;
}

export function showGamificationButton(): boolean {
  if (isGamificationDisabled()) {
    return false;
  }

  if (isGamificationMaintenanceEnabled()) {
    return true;
  }

  // Show gamification button if gamanza is configured
  if (window.$app.appConfig.gamanzaServiceUrl !== '') {
    return true;
  }

  return false;
}
